import { Tag } from 'antd'
import { DrilldownTableExtraColumn } from 'features/drilldown/components/table/config/DrilldownTableConfigContext'
import { DrilldownTableColumnDef } from 'features/drilldown/components/table/config/DrilldownTableSchema'
import {
  DEFAULT_TABLE_PANEL_SHARED_FIELD_DATA,
  DrilldownTablePanelData,
  LINK_TEMPLATE_COMPONENT,
} from 'features/drilldown/components/table/DrilldownTable'
import { PaginatedRolodexTableRecordType } from 'features/drilldown/components/table/PaginatedRolodexTable'
import {
  FfsLtaReport,
  FfsResultType,
  RESULT_OUTCOME_COLORS,
  RESULT_OUTCOME_LABELS,
} from 'features/indicationTriage'
import { FC, useMemo } from 'react'
import { GeckoEntry, RolodexEntryType } from 'types'

// FFS Level enum
enum FfsLevel {
  LEVEL_1 = '1',
  LEVEL_2 = '2',
}

type FfsResultTablePanelData = DrilldownTablePanelData

// Helper function to safely extract FFS data from GeckoEntry
const getFfsDataFromEntry = (entry: GeckoEntry): FfsLtaReport | undefined => {
  if (entry.type !== RolodexEntryType.FFS_LTA_REPORT) {
    return undefined
  }

  // Type-safe access to the data structure
  return entry.data as unknown as FfsLtaReport
}

const FFS_RESULT_TABLE_COLUMN_DEFS: ReadonlyArray<DrilldownTableColumnDef> = [
  {
    title: 'Component',
    field: 'component.name',
    linkTemplate: LINK_TEMPLATE_COMPONENT,
  },
  {
    title: 'Indication ID',
    field: 'indication.id',
    linkTemplate:
      '/details/{component.id}?extension=IndicationTriage&indication={indication.id}',
  },
  {
    title: 'Circ Length',
    field: 'data.data_values.latitudinalFlawLength.num',
  },
  {
    title: 'FFS Level 1 Report',
    field: 'data.outcome.scenario', // This will be overridden by custom column
  },
  {
    title: 'FFS Level 1 Result',
    field: 'data.outcome.result', // This will be overridden by custom column
  },
  {
    title: 'FFS Level 2 Report',
    field: 'data.outcome.scenario', // This will be overridden by custom column
  },
  {
    title: 'FFS Level 2 Result',
    field: 'data.outcome.result', // This will be overridden by custom column
  },
  {
    title: 'Repair Required',
    field: 'data.outcome.result', // This will be overridden by custom column
  },
]

const useGetFfsResultForLevel = (
  entries: readonly GeckoEntry[],
  level: FfsLevel
): FfsLtaReport | undefined => {
  const indicationEntry: GeckoEntry | undefined = useMemo(
    () =>
      entries.filter(
        (e: GeckoEntry) => e.type === RolodexEntryType.INDICATION
      )[0],
    [entries]
  )

  const ffsReportEntries: readonly GeckoEntry[] = useMemo(
    () =>
      entries.filter(
        (e: GeckoEntry) => e.type === RolodexEntryType.FFS_LTA_REPORT
      ),
    [entries]
  )

  if (!indicationEntry || ffsReportEntries.length === 0) {
    return undefined
  }

  const matchingFfsReports: GeckoEntry[] = ffsReportEntries.filter(
    (e: GeckoEntry) =>
      e.tags?.indication === indicationEntry.id && e.data['ffs_level'] === level
  )

  const defaultEntry: GeckoEntry | undefined = matchingFfsReports.find(
    (e: GeckoEntry) => e.data['is_default'] === true
  )

  // Note: getting the first ffs report if no default is found
  // TODO @choikh: Allow user to select which ffs report to use
  const selectedEntry = defaultEntry ?? matchingFfsReports[0]

  return selectedEntry ? getFfsDataFromEntry(selectedEntry) : undefined
}

const RepairRequiredCol: FC<{ rec: PaginatedRolodexTableRecordType }> = ({
  rec: { entries },
}) => {
  const level1Result: FfsLtaReport | undefined = useGetFfsResultForLevel(
    entries,
    FfsLevel.LEVEL_1
  )

  const level2Result: FfsLtaReport | undefined = useGetFfsResultForLevel(
    entries,
    FfsLevel.LEVEL_2
  )

  // If both are failed (Repair), then repair is required
  if (
    level1Result?.outcome.result === FfsResultType.Fail &&
    level2Result?.outcome.result === FfsResultType.Fail
  ) {
    return <Tag color='red'>Required</Tag>
  }

  // If at least one passes (Pass or Derate), then repair is not required
  if (
    level1Result?.outcome.result === FfsResultType.Pass ||
    level1Result?.outcome.result === FfsResultType.Derate ||
    level2Result?.outcome.result === FfsResultType.Pass ||
    level2Result?.outcome.result === FfsResultType.Derate
  ) {
    return <Tag color='green'>Not Required</Tag>
  }

  // If we have mixed results or unknowns
  return <Tag color='gray'>Unknown</Tag>
}

// Color code based on result status using the centralized colors from the API
const getResultColor = (result: FfsResultType) => {
  return RESULT_OUTCOME_COLORS[result] ?? 'blue'
}

const FfsLevel1ReportCol: FC<{ rec: PaginatedRolodexTableRecordType }> = ({
  rec: { entries },
}) => {
  const level1Result: FfsLtaReport | undefined = useGetFfsResultForLevel(
    entries,
    FfsLevel.LEVEL_1
  )

  const scenario = level1Result?.outcome.scenario ?? ''
  return scenario
}

const FfsLevel1ResultCol: FC<{ rec: PaginatedRolodexTableRecordType }> = ({
  rec: { entries },
}) => {
  const level1Result: FfsLtaReport | undefined = useGetFfsResultForLevel(
    entries,
    FfsLevel.LEVEL_1
  )

  const result: FfsResultType =
    (level1Result?.outcome.result as FfsResultType) ?? FfsResultType.Pending
  const resultLabel = RESULT_OUTCOME_LABELS[result] ?? result
  return <Tag color={getResultColor(result)}>{resultLabel}</Tag>
}

const FfsLevel2ReportCol: FC<{ rec: PaginatedRolodexTableRecordType }> = ({
  rec: { entries },
}) => {
  const level2Result: FfsLtaReport | undefined = useGetFfsResultForLevel(
    entries,
    FfsLevel.LEVEL_2
  )

  const scenario = level2Result?.outcome.scenario ?? ''
  return scenario
}

const FfsLevel2ResultCol: FC<{ rec: PaginatedRolodexTableRecordType }> = ({
  rec: { entries },
}) => {
  const level2Result: FfsLtaReport | undefined = useGetFfsResultForLevel(
    entries,
    FfsLevel.LEVEL_2
  )

  const result: FfsResultType =
    (level2Result?.outcome.result as FfsResultType) ?? FfsResultType.Pending
  const resultLabel = RESULT_OUTCOME_LABELS[result] ?? result
  return <Tag color={getResultColor(result)}>{resultLabel}</Tag>
}

export const FFS_RESULT_TABLE_EXTRA_COLS: ReadonlyArray<DrilldownTableExtraColumn> =
  [
    {
      id: 'col_ffs_level_1_report',
      name: 'FFS L1 Report',
      linked_types: [
        RolodexEntryType.INDICATION,
        RolodexEntryType.FFS_LTA_REPORT,
      ],
      render: (props) => <FfsLevel1ReportCol {...props} />,
    },
    {
      id: 'col_ffs_level_1_result',
      name: 'FFS L1 Result',
      linked_types: [
        RolodexEntryType.INDICATION,
        RolodexEntryType.FFS_LTA_REPORT,
      ],
      render: (props) => <FfsLevel1ResultCol {...props} />,
    },
    {
      id: 'col_ffs_level_2_report',
      name: 'FFS L2 Report',
      linked_types: [
        RolodexEntryType.INDICATION,
        RolodexEntryType.FFS_LTA_REPORT,
      ],
      render: (props) => <FfsLevel2ReportCol {...props} />,
    },
    {
      id: 'col_ffs_level_2_result',
      name: 'FFS L2 Result',
      linked_types: [
        RolodexEntryType.INDICATION,
        RolodexEntryType.FFS_LTA_REPORT,
      ],
      render: (props) => <FfsLevel2ResultCol {...props} />,
    },
    {
      id: 'col_repair_required_status',
      name: 'Repair Required',
      linked_types: [
        RolodexEntryType.INDICATION,
        RolodexEntryType.FFS_LTA_REPORT,
      ],
      render: (props) => <RepairRequiredCol {...props} />,
    },
  ]

export const DEFAULT_FFS_RESULT_TABLE_PANEL_DATA: FfsResultTablePanelData = {
  ...DEFAULT_TABLE_PANEL_SHARED_FIELD_DATA,
  title: 'FFS Results',
  isPinned: true,
  columns: FFS_RESULT_TABLE_COLUMN_DEFS,
  groupByType: RolodexEntryType.INDICATION,
  enabledExtraColumnIds: [
    'col_repair_required_status',
    'col_ffs_level_1_report',
    'col_ffs_level_1_result',
    'col_ffs_level_2_report',
    'col_ffs_level_2_result',
  ],
}
